import React, { useState, useEffect, useCallback } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import type { StablecoinConfigItem } from '../../services/adminApiService';
import './AdminComponents.css';

// ─── Helpers ────────────────────────────────────────────────────────────────
const NETWORKS = ['ETHEREUM', 'SOLANA', 'POLYGON', 'STELLAR', 'CARDANO', 'BITCOIN'];
const COMMON_COINS = ['USDC', 'USDT', 'DAI', 'EURX', 'BUSD', 'FRAX'];

const emptyForm = (): Partial<StablecoinConfigItem> => ({
  symbol: '',
  name: '',
  network: 'ETHEREUM',
  enabled: true,
  transactionLimit: null,
  dailyLimit: null,
  minimumAmount: 0,
  description: '',
  contractAddress: '',
  icon: '',
  convertEnabled: true,
  sendEnabled: true,
  receiveEnabled: true,
});

const networkColor: Record<string, string> = {
  ETHEREUM: '#627eea',
  SOLANA:   '#9945ff',
  POLYGON:  '#8247e5',
  STELLAR:  '#7fcef9',
  CARDANO:  '#0d3349',
  BITCOIN:  '#f7931a',
};

// ─── Component ───────────────────────────────────────────────────────────────
const AdminStablecoinManagement: React.FC = () => {
  const adminApi = useAdminApiService();
  const [configs, setConfigs] = useState<StablecoinConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter / search
  const [filterNetwork, setFilterNetwork] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<StablecoinConfigItem>>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getStablecoinConfigs();
      if (res.success && res.data) {
        setConfigs(res.data);
      } else {
        setError(res.error || 'Failed to load stablecoin configurations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load stablecoin configurations');
    } finally {
      setLoading(false);
    }
  }, [adminApi]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  // Auto-clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 4000); return () => clearTimeout(t); }
  }, [success]);

  // ── Toggle enable / disable ───────────────────────────────────────────────
  const handleToggle = async (config: StablecoinConfigItem) => {
    try {
      const res = await adminApi.setStablecoinConfigStatus(config.id, !config.enabled);
      if (res.success && res.data) {
        setConfigs(prev => prev.map(c => c.id === config.id ? res.data! : c));
        setSuccess(`${config.symbol} on ${config.network} ${!config.enabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        setError(res.error || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (config: StablecoinConfigItem) => {
    if (!window.confirm(`Permanently delete ${config.symbol} on ${config.network}?`)) return;
    try {
      const res = await adminApi.deleteStablecoinConfig(config.id);
      if (res.success) {
        setConfigs(prev => prev.filter(c => c.id !== config.id));
        setSuccess(`${config.symbol} on ${config.network} deleted`);
      } else {
        setError(res.error || 'Failed to delete');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  // ── Open modal ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (config: StablecoinConfigItem) => {
    setEditingId(config.id);
    setForm({ ...config });
    setFormError(null);
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.symbol?.trim()) { setFormError('Symbol is required'); return; }
    if (!form.name?.trim())   { setFormError('Name is required'); return; }
    if (!form.network?.trim()) { setFormError('Network is required'); return; }

    setSaving(true);
    setFormError(null);
    try {
      if (editingId !== null) {
        const res = await adminApi.updateStablecoinConfig(editingId, form);
        if (res.success && res.data) {
          setConfigs(prev => prev.map(c => c.id === editingId ? res.data! : c));
          setSuccess(`${form.symbol} on ${form.network} updated successfully`);
          setShowModal(false);
        } else {
          setFormError(res.error || 'Failed to update');
        }
      } else {
        const res = await adminApi.createStablecoinConfig(form);
        if (res.success && res.data) {
          setConfigs(prev => [...prev, res.data!]);
          setSuccess(`${form.symbol} on ${form.network} created successfully`);
          setShowModal(false);
        } else {
          setFormError(res.error || 'Failed to create');
        }
      }
    } catch (err: any) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = configs.filter(c => {
    if (filterNetwork !== 'ALL' && c.network !== filterNetwork) return false;
    if (filterStatus === 'ENABLED'  && !c.enabled) return false;
    if (filterStatus === 'DISABLED' && c.enabled)  return false;
    if (searchTerm && !`${c.symbol} ${c.name} ${c.network}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalEnabled  = configs.filter(c => c.enabled).length;
  const totalDisabled = configs.filter(c => !c.enabled).length;
  const networks = Array.from(new Set(configs.map(c => c.network)));

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' }}>
            💱 Stablecoin Management
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Control which stablecoins are available to retail users and configure per-coin limits
          </p>
        </div>
        <button className="primary-btn" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          ➕ Add Stablecoin
        </button>
      </div>

      {/* ── Alerts ── */}
      {error && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}
      {success && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#065f46', fontSize: 14 }}>
          ✅ {success}
        </div>
      )}

      {/* ── Stats Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Configs',  value: configs.length,  color: '#3b82f6', icon: '📋' },
          { label: 'Enabled',        value: totalEnabled,    color: '#10b981', icon: '✅' },
          { label: 'Disabled',       value: totalDisabled,   color: '#ef4444', icon: '🚫' },
          { label: 'Networks',       value: networks.length, color: '#8b5cf6', icon: '🌐' },
        ].map(stat => (
          <div key={stat.label} style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{stat.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search symbol, name, network..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: '1 1 200px', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <select
          value={filterNetwork}
          onChange={e => setFilterNetwork(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
        >
          <option value="ALL">All Networks</option>
          {networks.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
        >
          <option value="ALL">All Statuses</option>
          <option value="ENABLED">Enabled Only</option>
          <option value="DISABLED">Disabled Only</option>
        </select>
        <button onClick={loadConfigs} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, cursor: 'pointer', background: '#f8fafc' }}>
          🔄 Refresh
        </button>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
          Loading configurations...
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Stablecoin', 'Network', 'Status', 'Operations', 'Limits', 'Contract', 'Updated', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                    No stablecoin configurations found
                  </td>
                </tr>
              ) : filtered.map(config => (
                <tr key={config.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  {/* Stablecoin */}
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{config.icon || '🪙'}</span>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{config.symbol}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{config.name}</div>
                      </div>
                    </div>
                  </td>
                  {/* Network */}
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: (networkColor[config.network] || '#6b7280') + '22',
                      color: networkColor[config.network] || '#6b7280',
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>
                      {config.network}
                    </span>
                  </td>
                  {/* Status toggle */}
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      onClick={() => handleToggle(config)}
                      style={{
                        padding: '4px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                        background: config.enabled ? '#d1fae5' : '#fee2e2',
                        color: config.enabled ? '#065f46' : '#991b1b',
                      }}
                    >
                      {config.enabled ? '✅ Enabled' : '🚫 Disabled'}
                    </button>
                  </td>
                  {/* Operations */}
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {config.sendEnabled    && <span style={opBadge('#dbeafe','#1d4ed8')}>Send</span>}
                      {config.receiveEnabled && <span style={opBadge('#ede9fe','#6d28d9')}>Receive</span>}
                      {config.convertEnabled && <span style={opBadge('#fef3c7','#92400e')}>Convert</span>}
                      {!config.sendEnabled && !config.receiveEnabled && !config.convertEnabled && (
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>None</span>
                      )}
                    </div>
                  </td>
                  {/* Limits */}
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                    {config.transactionLimit ? <div>Tx: ${config.transactionLimit.toLocaleString()}</div> : null}
                    {config.dailyLimit       ? <div>Daily: ${config.dailyLimit.toLocaleString()}</div> : null}
                    {config.minimumAmount > 0 ? <div>Min: ${config.minimumAmount}</div> : null}
                    {!config.transactionLimit && !config.dailyLimit && config.minimumAmount === 0 && (
                      <span style={{ color: '#cbd5e1' }}>No limits set</span>
                    )}
                  </td>
                  {/* Contract */}
                  <td style={{ padding: '12px 16px', fontSize: 11, color: '#94a3b8', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {config.contractAddress
                      ? <code title={config.contractAddress}>{config.contractAddress.slice(0, 10)}…</code>
                      : <span>—</span>}
                  </td>
                  {/* Updated */}
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                    {config.updatedAt ? new Date(config.updatedAt).toLocaleDateString() : '—'}
                  </td>
                  {/* Actions */}
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(config)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}>
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDelete(config)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#dc2626' }}>
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {editingId !== null ? '✏️ Edit Stablecoin Config' : '➕ Add Stablecoin Config'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#64748b' }}>×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'grid', gap: 16 }}>
              {formError && (
                <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13 }}>
                  ⚠️ {formError}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <FormField label="Symbol *">
                  <select value={form.symbol || ''} onChange={e => setForm(f => ({ ...f, symbol: e.target.value }))} style={inputStyle}>
                    <option value="">Select or type…</option>
                    {COMMON_COINS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="text" placeholder="Or enter custom symbol" value={form.symbol || ''}
                    onChange={e => setForm(f => ({ ...f, symbol: e.target.value.toUpperCase() }))} style={{ ...inputStyle, marginTop: 6 }} />
                </FormField>

                <FormField label="Network *">
                  <select value={form.network || 'ETHEREUM'} onChange={e => setForm(f => ({ ...f, network: e.target.value }))} style={inputStyle}>
                    {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </FormField>
              </div>

              <FormField label="Full Name *">
                <input type="text" placeholder="e.g. USD Coin" value={form.name || ''}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
              </FormField>

              <FormField label="Description">
                <textarea placeholder="Describe this stablecoin for retail users…" value={form.description || ''}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ ...inputStyle, height: 72, resize: 'vertical' }} />
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <FormField label="Contract Address">
                  <input type="text" placeholder="0x… or program ID" value={form.contractAddress || ''}
                    onChange={e => setForm(f => ({ ...f, contractAddress: e.target.value }))} style={inputStyle} />
                </FormField>
                <FormField label="Icon / Emoji">
                  <input type="text" placeholder="Ξ  S  P  🪙" value={form.icon || ''}
                    onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} style={inputStyle} />
                </FormField>
              </div>

              {/* Limits */}
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#374151' }}>💰 Transaction Limits</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <FormField label="Min Amount ($)">
                    <input type="number" min={0} value={form.minimumAmount ?? 0}
                      onChange={e => setForm(f => ({ ...f, minimumAmount: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                  </FormField>
                  <FormField label="Max per Tx ($)">
                    <input type="number" min={0} placeholder="No limit" value={form.transactionLimit ?? ''}
                      onChange={e => setForm(f => ({ ...f, transactionLimit: e.target.value ? parseFloat(e.target.value) : null }))} style={inputStyle} />
                  </FormField>
                  <FormField label="Daily Limit ($)">
                    <input type="number" min={0} placeholder="No limit" value={form.dailyLimit ?? ''}
                      onChange={e => setForm(f => ({ ...f, dailyLimit: e.target.value ? parseFloat(e.target.value) : null }))} style={inputStyle} />
                  </FormField>
                </div>
              </div>

              {/* Operations */}
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#374151' }}>⚙️ Allowed Operations</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {([
                    ['sendEnabled',    '📤 Send'],
                    ['receiveEnabled', '📥 Receive'],
                    ['convertEnabled', '🔄 Convert'],
                  ] as [keyof StablecoinConfigItem, string][]).map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                      <input type="checkbox" checked={!!(form as any)[key]}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Enable toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                <input type="checkbox" checked={!!form.enabled}
                  onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} style={{ width: 18, height: 18 }} />
                Enable immediately (visible to retail users)
              </label>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: saving ? '#93c5fd' : '#3b82f6', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Saving…' : editingId !== null ? 'Update Config' : 'Create Config'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Small helpers ─────────────────────────────────────────────────────────
const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const opBadge = (bg: string, color: string): React.CSSProperties => ({
  background: bg,
  color,
  padding: '2px 8px',
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

export default AdminStablecoinManagement;
